/**
 * Blackout Contracts UI (M3 S6) — the browser wrapper over blackout-core.js.
 *
 * Two surfaces, split the way the spec describes: contracts are ARMED from the
 * raid planner (L1), and the catalog of what has been earned lives on the
 * Syndicate screen (L3). That is the "layers reach into lower layers" rule from
 * LayerNavigation.md — an L3 feature putting a control on an L1 screen — and it
 * is why arming does not live on the Syndicate page where it would be three
 * navigations away from the raid it modifies.
 *
 * All rules live in blackout-core.js; this file renders and reports.
 */

import * as config from '../constants.js';
import { gameState, saveGameState } from './gameState.js';
import { addNotification } from './notifications.js';
import {
    isBlackoutUnlocked, blackoutContractIds, getBlackoutContract, isBlackoutCleared,
    blackoutBlockedReason, activeBlackout, setBlackoutContract,
} from './blackout-core.js';

const NIGHT = '#8b7fd4';
const NIGHT_DIM = '#4a4270';

const BLOCKED_COPY = {
    'tier-too-low': (c) => `Tier ${c.minTier}+`,
    'already-cleared': () => '✓ Cleared',
    'no-sanction': () => 'Unlicensed',
};

/**
 * The arming strip on the raid planner.
 *
 * Hidden entirely without the charter rather than shown disabled: an unsigned
 * charter is not a state the player can act on from here, and a permanently
 * dead panel on the most-used screen in the game is the dead control the layer
 * spec exists to avoid.
 */
export function renderBlackoutStrip() {
    const container = document.getElementById('blackout-strip');
    if (!container) return;

    if (!isBlackoutUnlocked()) {
        container.classList.add('hidden');
        container.innerHTML = '';
        return;
    }
    container.classList.remove('hidden');

    const tier = gameState.currentRaid?.selectedTier || 0;
    const armed = activeBlackout();
    // Arming is a SETUP decision — see setBlackoutContract, which refuses
    // mid-raid because the modifiers shape how the raid was assembled.
    const locked = !!gameState.currentRaid?.raidInProgress;

    const cards = blackoutContractIds().map(id => {
        const c = getBlackoutContract(id);
        const reason = blackoutBlockedReason(id, tier);
        const cleared = isBlackoutCleared(id);
        const isArmed = armed?.id === id;
        const selectable = !reason && !locked;

        return `
            <button type="button" ${selectable ? `onclick="armBlackout('${isArmed ? '' : id}')"` : 'disabled'}
                class="text-left rounded-lg px-2 py-1.5 border-2 ${selectable ? '' : 'opacity-60 cursor-not-allowed'}"
                title="${c.description}${cleared ? ' — already cleared' : ''}"
                style="border-color:${isArmed ? NIGHT : NIGHT_DIM}; background:${isArmed ? 'rgba(139,127,212,0.18)' : 'rgba(255,255,255,0.04)'}">
                <div class="flex items-baseline justify-between gap-2">
                    <span class="font-bold text-xs" style="color:${isArmed ? NIGHT : '#c9c2ee'}">${c.icon} ${c.name}</span>
                    ${reason ? `<span class="text-[10px] ${cleared ? 'text-emerald-400' : 'text-gray-400'}">${BLOCKED_COPY[reason]?.(c) || ''}</span>` : ''}
                </div>
                <div class="text-[10px] text-gray-400 leading-tight">${c.perk.label}</div>
            </button>`;
    }).join('');

    container.innerHTML = `
        <div class="rounded-lg p-2" style="background:rgba(139,127,212,0.07); border:2px solid ${NIGHT_DIM};">
            <div class="flex items-baseline justify-between gap-2 mb-1.5">
                <div class="text-[11px] uppercase tracking-widest" style="color:${NIGHT}">🌑 Blackout Contract</div>
                <div class="text-xs text-gray-400">
                    ${armed
                        ? `<span style="color:${NIGHT}">${armed.icon} ${armed.name}</span> armed — ${armed.description}`
                        : 'Optional. Harder raid, one permanent perk. Click to arm.'}
                </div>
            </div>
            <div class="grid gap-1.5" style="grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));">${cards}</div>
        </div>`;
}

/** Arm or disarm from the strip. Empty id disarms. */
export function armBlackout(contractId) {
    const result = setBlackoutContract(contractId || null);
    if (!result.ok) {
        const copy = {
            'raid-in-progress': 'Cannot change contract mid-raid.',
            'tier-too-low': 'That contract needs a higher tier.',
            'already-cleared': 'Already cleared — its perk is permanent.',
            'no-sanction': 'Sign the Blackout Sanction charter first.',
        };
        addNotification(copy[result.reason] || 'Cannot arm that contract', 'error');
        return;
    }
    if (result.contract) {
        addNotification(`🌑 ${result.contract.name} armed — ${result.contract.description}`, 'warning');
    }
    saveGameState();
    renderBlackoutStrip();
}

/**
 * The catalog on the Syndicate Charter page: what each contract asks and what
 * it pays, with the cleared ones marked.
 *
 * Deliberately shows LOCKED and CLEARED contracts too. Five finite one-time
 * perks are a checklist, and a checklist that hides its remaining items is a
 * list of things the player does not know exist.
 */
export function renderBlackoutCatalog() {
    const container = document.getElementById('syndicate-blackout');
    if (!container) return;

    if (!isBlackoutUnlocked()) {
        container.innerHTML = `<div class="text-sm text-gray-400">Sign the Blackout Sanction charter to take on contracts.</div>`;
        return;
    }

    container.innerHTML = blackoutContractIds().map(id => {
        const c = getBlackoutContract(id);
        const cleared = isBlackoutCleared(id);
        return `
            <div class="rounded-lg p-3" style="background:rgba(139,127,212,${cleared ? '0.12' : '0.05'}); border:2px solid ${cleared ? NIGHT : NIGHT_DIM};">
                <div class="flex items-baseline justify-between gap-2">
                    <span class="font-bold text-sm" style="color:${NIGHT}">${c.icon} ${c.name}</span>
                    <span class="text-xs ${cleared ? 'text-emerald-400' : 'text-gray-400'}">${cleared ? '✓ Cleared' : `Tier ${c.minTier}+`}</span>
                </div>
                <div class="text-xs text-gray-400 italic leading-snug mt-0.5">${c.flavor}</div>
                <div class="text-xs text-gray-300 leading-snug mt-1">${c.description}</div>
                <div class="text-xs mt-1.5 font-bold" style="color:${cleared ? '#7df5a0' : NIGHT}">${cleared ? '✓ ' : '🎁 '}${c.perk.label}</div>
            </div>`;
    }).join('');
}

if (typeof window !== 'undefined') {
    window.armBlackout = armBlackout;
}
